import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BookOpen,
  Clock,
  ArrowRight,
  Search,
  MessageSquare,
  Menu,
  X,
  BarChart3,
  Zap,
  Bot,
  ChevronRight,
  Database
} from 'lucide-react';
import { BLOG_POSTS } from './BlogData';

/* ─── Category config ────────────────────────────────────── */

const ALL_CATEGORIES = [
  'Todos',
  'Automatización & IA',
  'Business Intelligence',
  'Inteligencia Artificial',
];

const CATEGORY_META = {
  'Automatización & IA':    { icon: Zap,       color: 'text-violet-600',  bg: 'bg-violet-50',  border: 'border-violet-200', strip: 'bg-violet-500'  },
  'Business Intelligence':  { icon: BarChart3,  color: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-200', strip: 'bg-emerald-500' },
  'Inteligencia Artificial':{ icon: Bot,        color: 'text-sky-600',     bg: 'bg-sky-50',     border: 'border-sky-200',    strip: 'bg-sky-500'    },
};

const getCatMeta = cat => CATEGORY_META[cat] || { icon: BookOpen, color: 'text-slate-500', bg: 'bg-slate-50', border: 'border-slate-200', strip: 'bg-slate-400' };


/* ─── Component ──────────────────────────────────────────── */

const BlogHubPage = () => {
  const [category, setCategory] = useState('Todos');
  const [query, setQuery]       = useState('');
  const [menuOpen, setMenuOpen] = useState(false);

  const filtered = BLOG_POSTS.filter(p => {
    const matchCat = category === 'Todos' || p.category === category;
    const matchQ   = !query || [p.title, p.subtitle, p.keywords].join(' ').toLowerCase().includes(query.toLowerCase());
    return matchCat && matchQ;
  });

  const featured = category === 'Todos' && !query ? BLOG_POSTS[0] : null;
  const grid     = featured ? filtered.filter(p => p.slug !== featured.slug) : filtered;

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <Helmet>
        <title>Blog & Guías de Transformación Digital | NeuraSur</title>
        <meta name="description" content="Guías prácticas sobre automatización, Power BI, chatbots con IA y transformación digital para pymes argentinas." />
        <link rel="canonical" href="https://www.neurasur.com/blog" />
      </Helmet>

      {/* ── Navbar ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-[1200px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <div className="bg-slate-50 border border-slate-200 p-1.5 rounded-lg">
              <img src="/logo.webp" onError={e => { e.target.src = '/logo.png'; }} alt="NeuraSur" className="h-7 w-auto" />
            </div>
            <span className="font-bold text-slate-900 text-[15px] tracking-tight">NeuraSur</span>
          </Link>

          <div className="hidden md:flex items-center gap-1">
            <Link
              to="/servicios/power-bi-business-intelligence"
              className="px-3.5 py-2 rounded-lg text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-50 transition-all"
            >
              Servicios
            </Link>
            <Link
              to="/blog"
              className="px-3.5 py-2 rounded-lg text-sm font-semibold text-emerald-700 bg-emerald-50/80 rounded-lg transition-all"
            >
              Blog
            </Link>
          </div>

          <div className="flex items-center gap-3">
            <a
              href="https://wa.me/5491171299730?text=Hola!%20Quiero%20hacer%20una%20consulta."
              target="_blank" rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 text-white px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
            >
              Hablar con un experto <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors text-slate-700"
              aria-label="Menú"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden overflow-hidden border-t border-slate-200 bg-white shadow-xl"
            >
              <div className="px-5 py-4 space-y-1 bg-white">
                <Link to="/servicios/power-bi-business-intelligence" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Servicios
                </Link>
                <Link to="/blog" onClick={() => setMenuOpen(false)} className="block px-3 py-2.5 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-50">
                  Blog
                </Link>
                <a href="https://wa.me/5491171299730?text=Hola!%20Quiero%20hacer%20una%20consulta." target="_blank" rel="noopener noreferrer" className="block text-center mt-3 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors">
                  Hablar con un experto
                </a>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ── Hero del Blog ── */}
      <section className="pt-28 pb-10 px-5 sm:px-8 border-b border-slate-100">
        <div className="max-w-[1200px] mx-auto">
          {/* Breadcrumb */}
          <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-10">
            <Link to="/" className="hover:text-slate-600 transition-colors">Inicio</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-slate-600">Blog & Guías</span>
          </div>

          <div className="grid lg:grid-cols-[1fr_340px] gap-12 items-center">
            {/* Copy */}
            <div>
              <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full mb-6 tracking-wide uppercase">
                <BookOpen className="w-3 h-3" /> Centro de conocimiento
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-[52px] font-extrabold text-slate-900 leading-[1.1] tracking-tight">
                Automatización,<br />Datos e IA para Pymes
              </h1>
              <p className="mt-4 text-base text-slate-500 leading-relaxed max-w-xl">
                Aprende cómo eliminar tareas manuales y tomar mejores decisiones con tecnología.
              </p>

              {/* Quick topics */}
              <div className="mt-6 flex flex-wrap gap-2">
                {['n8n & Automatización', 'Power BI', 'Chatbots IA', 'WhatsApp API'].map(t => (
                  <span key={t} className="text-xs font-medium text-slate-500 bg-slate-100 px-3 py-1 rounded-full">
                    {t}
                  </span>
                ))}
              </div>
            </div>

            {/* Visual stats panel */}
            <div className="hidden lg:block">
              <div className="bg-slate-50 border border-slate-200 rounded-2xl overflow-hidden">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
                  <Database className="w-3.5 h-3.5 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Publicaciones</span>
                </div>
                <div className="p-4 space-y-3">
                  {[
                    { cat: 'Automatización & IA',     count: 1, icon: Zap,      color: 'text-violet-600', bg: 'bg-violet-50' },
                    { cat: 'Business Intelligence',   count: 1, icon: BarChart3, color: 'text-emerald-600', bg: 'bg-emerald-50' },
                    { cat: 'Inteligencia Artificial', count: 1, icon: Bot,       color: 'text-sky-600', bg: 'bg-sky-50' },
                  ].map(item => {
                    const Icon = item.icon;
                    return (
                      <div key={item.cat} className="flex items-center gap-3">
                        <div className={`w-7 h-7 ${item.bg} rounded-lg flex items-center justify-center shrink-0`}>
                          <Icon className={`w-3.5 h-3.5 ${item.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium text-slate-700 truncate">{item.cat}</p>
                        </div>
                        <span className="text-xs font-bold text-slate-900">{item.count} art.</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Search + Filters */}
          <div className="mt-10 flex flex-col sm:flex-row gap-3 items-start sm:items-center">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <input
                type="text"
                placeholder="Buscar artículos…"
                value={query}
                onChange={e => setQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 transition-all"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {ALL_CATEGORIES.map(cat => (
                <button
                  key={cat}
                  onClick={() => setCategory(cat)}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-all shrink-0 border ${
                    category === cat
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300 hover:text-slate-800'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── Content ── */}
      <section className="py-10 px-5 sm:px-8 pb-20">
        <div className="max-w-[1200px] mx-auto">

          {/* ── Featured Article ── */}
          {featured && (
            <div className="mb-10">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Artículo destacado</span>
                <div className="flex-1 h-px bg-slate-100" />
              </div>

              <Link
                to={`/blog/${featured.slug}`}
                className="group bg-white border border-slate-200 hover:border-slate-300 rounded-2xl p-7 sm:p-9 block transition-all hover:shadow-md"
              >
                {/* Meta */}
                <div className="flex items-center gap-3 mb-4">
                  {(() => {
                    const meta = getCatMeta(featured.category);
                    const Icon = meta.icon;
                    return (
                      <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-md ${meta.color} ${meta.bg} border ${meta.border}`}>
                        <Icon className="w-3 h-3" /> {featured.category}
                      </span>
                    );
                  })()}
                  <span className="flex items-center gap-1 text-xs text-slate-400">
                    <Clock className="w-3 h-3" /> {featured.readTime}
                  </span>
                </div>

                {/* Title */}
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 group-hover:text-emerald-700 transition-colors leading-tight tracking-tight">
                  {featured.title}
                </h2>

                {/* Subtitle */}
                <p className="mt-3 text-sm text-slate-500 leading-relaxed">
                  {featured.subtitle}
                </p>

                {/* Footer */}
                <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span className="font-medium text-slate-600">{featured.author.name}</span>
                    <span>·</span>
                    <span>{featured.publishedDate}</span>
                  </div>
                  <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-emerald-600 group-hover:gap-2.5 transition-all">
                    Leer artículo <ArrowRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            </div>
          )}

          {/* ── Article Grid ── */}
          {grid.length === 0 && !featured ? (
            <div className="text-center py-16 text-slate-400">
              <Search className="w-8 h-8 mx-auto mb-3 text-slate-300" />
              <p className="text-sm font-medium">No se encontraron artículos.</p>
              <button
                onClick={() => { setQuery(''); setCategory('Todos'); }}
                className="mt-3 text-xs text-emerald-600 hover:text-emerald-700 font-semibold"
              >
                Restablecer filtros
              </button>
            </div>
          ) : grid.length > 0 && (
            <>
              {featured && (
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">Más artículos</span>
                  <div className="flex-1 h-px bg-slate-100" />
                </div>
              )}
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {grid.map(post => {
                  const meta = getCatMeta(post.category);
                  const Icon = meta.icon;
                  return (
                    <Link
                      key={post.slug}
                      to={`/blog/${post.slug}`}
                      className="group flex flex-col bg-white border border-slate-200 hover:border-slate-300 hover:shadow-md rounded-2xl overflow-hidden transition-all"
                    >
                      {/* Category strip */}
                      <div className={`h-1 w-full ${meta.strip}`} />

                      <div className="flex flex-col flex-1 p-5">
                        {/* Meta */}
                        <div className="flex items-center gap-2.5 mb-3">
                          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-md ${meta.color} ${meta.bg} border ${meta.border}`}>
                            <Icon className="w-3 h-3" /> {post.category}
                          </span>
                          <span className="flex items-center gap-1 text-xs text-slate-400">
                            <Clock className="w-3 h-3" /> {post.readTime}
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="font-semibold text-[15px] text-slate-900 group-hover:text-emerald-700 transition-colors leading-snug tracking-tight">
                          {post.title}
                        </h3>

                        {/* Excerpt */}
                        <p className="mt-2 text-xs text-slate-500 line-clamp-2 leading-relaxed flex-1">
                          {post.subtitle}
                        </p>

                        {/* Card footer */}
                        <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                          <span className="text-xs text-slate-400">{post.publishedDate}</span>
                          <span className="text-xs font-semibold text-emerald-600 flex items-center gap-1 group-hover:gap-1.5 transition-all">
                            Leer <ArrowRight className="w-3 h-3" />
                          </span>
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {/* ── CTA ── */}
          <div className="mt-14 bg-slate-900 rounded-2xl px-8 sm:px-12 py-12 text-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-[0.04]" style={{
              backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
              backgroundSize: '32px 32px'
            }} />
            <div className="relative">
              <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4">Diagnóstico gratuito</p>
              <h3 className="text-xl sm:text-2xl font-bold text-white tracking-tight">¿Querés aplicar esto en tu empresa?</h3>
              <p className="mt-2 text-sm text-slate-400 max-w-md mx-auto leading-relaxed">
                30 minutos para identificar por dónde empezar y cuál es el ROI esperado.
              </p>
              <a
                href="https://wa.me/5491171299730?text=Hola!%20Quiero%20un%20diagnóstico%20de%20procesos."
                target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-7 bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600 text-white px-6 py-3.5 rounded-lg text-sm font-semibold transition-colors shadow-lg shadow-emerald-900/30"
              >
                <MessageSquare className="w-4 h-4" /> Solicitar diagnóstico gratuito
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="border-t border-slate-200 py-8 px-5 sm:px-8 bg-slate-50/50">
        <div className="max-w-[1200px] mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-600 font-medium">
          <p>© 2026 NeuraSur. Todos los derechos reservados.</p>
          <p className="text-slate-500">
            Diseño e implementación por{' '}
            <a 
              href="https://www.neurasur.com" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="text-emerald-600 font-semibold hover:text-emerald-700 transition-colors underline-offset-4 hover:underline"
            >
              NeuraSur
            </a>
          </p>
          <div className="flex gap-6">
            <Link to="/" className="hover:text-emerald-600 transition-colors">Inicio</Link>
            <Link to="/servicios/power-bi-business-intelligence" className="hover:text-emerald-600 transition-colors">Power BI</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default BlogHubPage;
